/**
 * Contacts Management Hook
 * 
 * Provides all contact-related operations (customers and vendors)
 */

import { useState, useCallback } from 'react';
import { useFinanceAPI } from './useFinanceAPI';
import type { ContactAccountStatus } from '@/lib/contacts/accountStanding';

export interface Contact {
  id?: number;
  contact_type: 'customer' | 'supplier' | 'partner' | 'contractor' | 'employee' | 'lead' | 'prospect' | 'visitor' | 'other';
  contact_number?: string;
  
  // Name fields (contacts table uses company_name + contact_person)
  company_name?: string;
  contact_person?: string;
  display_name?: string; // Computed: company_name or contact_person
  first_name?: string;   // Kept for compatibility
  last_name?: string;    // Kept for compatibility
  
  // Contact details
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  website?: string;
  tax_number?: string;
  registration_number?: string;
  tax_rate_id?: number;
  
  // Billing address
  billing_address?: string; // Combined address line
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_postal_code?: string;
  billing_country?: string;
  
  // Shipping address
  shipping_address?: string; // Combined address line
  shipping_address_line1?: string;
  shipping_address_line2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_postal_code?: string;
  shipping_country?: string;
  
  // Financial settings
  payment_terms?: string | number;  // COD, FOB, 7_days, 14_days, 30_days, etc.
  payment_terms_other?: string;  // Custom terms when "other" is selected
  payment_terms_days?: number;
  credit_limit?: number;
  currency?: string;
  current_balance?: number;
  default_receivable_account_id?: number;
  default_payable_account_id?: number;
  price_list_work_item_id?: string;
  account_status?: ContactAccountStatus;
  
  // Status
  is_active: boolean;
  notes?: string;
  special_instructions?: string;
  
  // Portal settings
  portal_enabled?: boolean;
  portal_type?: string;
  portal_permissions?: any;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  created_by?: number;
  updated_by?: number;
}

export function useContacts() {
  const api = useFinanceAPI();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [currentContact, setCurrentContact] = useState<Contact | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const loadContacts = useCallback(async (filters?: {
    contact_type?: 'customer' | 'supplier' | 'both' | 'all';
    is_active?: boolean;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const where: any = {};
    
    if (filters?.contact_type && filters.contact_type !== 'all') {
      if (filters.contact_type === 'both') {
        // 'both' means show both customers and suppliers
        where.contact_type = {
          operator: 'in',
          value: ['customer', 'supplier']
        };
      } else if (filters.contact_type === 'supplier') {
        where.contact_type = 'supplier';
      } else {
        // Single contact type (customer)
        where.contact_type = filters.contact_type;
      }
    }
    
    if (filters?.is_active !== undefined) {
      where.is_active = filters.is_active;
    }

    // Define searchable fields for contacts
    // Note: contacts table uses company_name + contact_person, not display_name
    const searchFields = ['company_name', 'contact_person', 'email', 'phone'];

    const result = await api.list<Contact>('contacts', {
      where,
      search: filters?.search, // Pass search term
      searchFields: searchFields, // Pass fields to search across
      orderBy: [{ field: 'company_name', direction: 'asc' }],
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25
    });

    if (result) {
      setContacts(result.data);
      if (result.pagination) {
        setPagination(result.pagination);
      }
    }

    return result;
  }, [api]);

  const loadContact = useCallback(async (id: number) => {
    const contact = await api.read<Contact>('contacts', { id });
    if (contact) {
      setCurrentContact(contact);
    }
    return contact;
  }, [api]);

  const createContact = useCallback(async (contact: Partial<Contact>) => {
    const createdContact = await api.create<Contact>('contacts', {
      payment_terms: '30_days',
      currency: 'ZAR',
      is_active: true,
      account_status: 'active',
      ...contact
    });
    
    if (createdContact) {
      await loadContacts();
    }
    
    return createdContact;
  }, [api, loadContacts]);

  const updateContact = useCallback(async (id: number, contact: Partial<Contact>) => {
    const updatedContact = await api.update('contacts', { id }, contact);
    
    if (updatedContact) {
      await loadContacts();
    }
    
    return updatedContact;
  }, [api, loadContacts]);

  const deleteContact = useCallback(async (id: number) => {
    // Soft delete - set is_active to false
    return updateContact(id, { is_active: false });
  }, [updateContact]);

  const getCustomers = useCallback(async () => {
    return loadContacts({ contact_type: 'customer' });
  }, [loadContacts]);

  const getSuppliers = useCallback(async () => {
    return loadContacts({ contact_type: 'supplier' });
  }, [loadContacts]);

  const searchContacts = useCallback(async (search: string) => {
    return loadContacts({ search });
  }, [loadContacts]);

  return {
    // State
    contacts,
    currentContact,
    pagination,
    loading: api.loading,
    error: api.error,
    
    // Actions
    loadContacts,
    loadContact,
    createContact,
    updateContact,
    deleteContact,
    getCustomers,
    getSuppliers,
    searchContacts
  };
}
